import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/caesar";
import { Loading, EmptyState, Pill } from "@/components/common";
import { toast } from "sonner";
import { AlertTriangle, Check, UserPlus, Clock, ArrowUpRight, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Labeled } from "@/components/common";

const SEV_COLOR = { high: "#E11D48", medium: "#F59E0B", low: "#A3A3A3" };

export default function Alerts() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ problem: "", why_it_matters: "", recommended_action: "", owner: "", severity: "high" });

  const load = useCallback(async () => { const { data } = await api.get("/alerts"); setAlerts(data); }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, updates, msg) => { await api.put(`/alerts/${id}`, updates); toast.success(msg); load(); };
  const resolve = (id) => act(id, { status: "resolved" }, "Resolved");
  const snooze = (id) => act(id, { status: "snoozed", snooze_until: new Date(Date.now() + 864e5).toISOString() }, "Snoozed 24h");
  const delegate = (a) => { const o = window.prompt("Delegate to:", a.owner || ""); if (o != null) act(a.id, { owner: o }, `Delegated to ${o}`); };

  const create = async () => {
    if (!form.problem.trim()) { toast.error("Describe the problem"); return; }
    await api.post("/alerts", form); toast.success("Alert created"); setOpen(false);
    setForm({ problem: "", why_it_matters: "", recommended_action: "", owner: "", severity: "high" }); load();
  };

  if (!alerts) return <Loading label="Scanning for threats…" />;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Red Alerts</h1>
          <p className="text-[#A3A3A3] mt-1 text-sm">Critical issues that demand attention. When red means something.</p>
        </div>
        <button data-testid="alert-create-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-4 h-10 rounded-lg border border-edge text-sm font-semibold hover:border-gold/30 transition-colors"><Plus size={16} /> New</button>
      </div>

      {alerts.length === 0 ? (
        <EmptyState testId="alerts-empty" title="No Red Alerts identified." hint="The Empire is secure." />
      ) : (
        <div className="space-y-3">
          {alerts.map((a) => (
            <div key={a.id} data-testid={`alert-${a.id}`} className="bg-surface border border-edge rounded-xl p-4 border-l-2" style={{ borderLeftColor: SEV_COLOR[a.severity] || "#E11D48" }}>
              <div className="flex items-start gap-3">
                <AlertTriangle size={18} className="mt-0.5 shrink-0" style={{ color: SEV_COLOR[a.severity] || "#E11D48" }} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold">{a.problem}</p>
                  {a.why_it_matters && <p className="text-sm text-[#A3A3A3] mt-1">{a.why_it_matters}</p>}
                  {a.recommended_action && <p className="text-sm mt-2"><span className="label-caps">Next: </span>{a.recommended_action}</p>}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {a.owner && <Pill color="#A3A3A3">{a.owner}</Pill>}
                    {a.auto && <Pill color="#D4AF37">auto</Pill>}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button data-testid={`alert-resolve-${a.id}`} onClick={() => resolve(a.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-sgreen text-ink text-xs font-bold"><Check size={13} /> Resolve</button>
                    <button onClick={() => delegate(a)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-edge text-xs font-semibold text-[#A3A3A3] hover:text-[#FAFAFA]"><UserPlus size={13} /> Delegate</button>
                    <button data-testid={`alert-snooze-${a.id}`} onClick={() => snooze(a.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-edge text-xs font-semibold text-[#A3A3A3] hover:text-[#FAFAFA]"><Clock size={13} /> Snooze</button>
                    {(a.money_id || a.project_id || a.person_id) && (
                      <button onClick={() => navigate(a.money_id ? "/money" : a.person_id ? "/people" : "/projects")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-edge text-xs font-semibold text-[#A3A3A3] hover:text-[#FAFAFA]"><ArrowUpRight size={13} /> Open</button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-edge">
          <DialogHeader><DialogTitle className="font-display text-xl">New Red Alert</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Labeled label="Problem"><Input value={form.problem} onChange={(e) => setForm({ ...form, problem: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <Labeled label="Why it matters"><Input value={form.why_it_matters} onChange={(e) => setForm({ ...form, why_it_matters: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <Labeled label="Recommended action"><Input value={form.recommended_action} onChange={(e) => setForm({ ...form, recommended_action: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <div className="grid grid-cols-2 gap-3">
              <Labeled label="Owner"><Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Severity">
                <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="w-full h-9 rounded-md bg-ink border border-edge px-2 text-sm"><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select>
              </Labeled>
            </div>
            <button data-testid="alert-save" onClick={create} className="w-full h-11 rounded-lg bg-gold text-ink font-bold">Create Alert</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
